import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageCircle, Facebook } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API } from '../App';
import { toast } from 'sonner';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Message sent! We'll get back to you soon.");
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'sarah@glowandgathercandles.co.uk',
      href: 'mailto:sarah@glowandgathercandles.co.uk'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '07703 390971',
      href: 'https://wa.me/447703390971'
    },
    {
      icon: Facebook,
      title: 'Facebook',
      value: 'Glow & Gather Candles',
      href: 'https://www.facebook.com/glowandgathercandles'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Scotland, United Kingdom',
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#5D6D53] py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-cursive text-2xl text-[#E5C88A] mb-2"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl text-white mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/80 max-w-2xl mx-auto"
          >
            Have a question about our products or need help with an order? We'd love to hear from you!
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="font-serif text-2xl text-[#2C2C2C] mb-6">Contact Information</h2>
              <p className="text-[#595959] mb-8">
                Feel free to reach out via email or WhatsApp. We typically respond within 24 hours.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#8C8C8C] mb-1">{item.title}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('https') ? '_blank' : undefined}
                          rel={item.href.startsWith('https') ? 'noopener noreferrer' : undefined}
                          className="text-[#2C2C2C] hover:text-[#C5A059] transition-colors font-medium"
                          data-testid={`contact-${item.title.toLowerCase()}`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-[#2C2C2C] font-medium">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* WhatsApp CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#25D366]/10 rounded-xl p-6"
            >
              <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">Quick Chat?</h3>
              <p className="text-sm text-[#595959] mb-4">
                For faster responses, reach out on WhatsApp!
              </p>
              <a
                href="https://wa.me/447703390971"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#25D366] hover:bg-[#20BA5A] text-white w-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-100">
              <h2 className="font-serif text-2xl text-[#2C2C2C] mb-6">Send Us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-glow mt-1"
                      placeholder="John Smith"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Your Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-glow mt-1"
                      placeholder="john@example.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="input-glow mt-1"
                    placeholder="How can we help?"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="input-glow mt-1 min-h-[150px]"
                    placeholder="Tell us more about your enquiry..."
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full md:w-auto"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* FAQ Teaser */}
      <section className="py-24 px-6 md:px-12 bg-[#F5F1EB] grain-texture">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-cursive text-2xl text-[#C5A059] mb-2">Common Questions</p>
            <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">Frequently Asked</h2>

            <div className="grid md:grid-cols-2 gap-6 text-left">
              {[
                {
                  q: 'How long do your candles burn?',
                  a: 'Our candles have varying burn times depending on size, typically between 30-50 hours for our standard sizes.'
                },
                {
                  q: 'Do you offer gift wrapping?',
                  a: 'Yes! All our products come beautifully packaged, perfect for gifting. We also offer special gift bundles.'
                },
                {
                  q: 'What wax do you use?',
                  a: 'We use 100% natural soy wax, which burns cleaner and longer than paraffin wax.'
                },
                {
                  q: 'Do you ship internationally?',
                  a: 'Currently, we ship throughout the UK. International shipping coming soon!'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                  <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">{faq.q}</h3>
                  <p className="text-[#595959] text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
